import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
            return;
        }

        // Check if iOS
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        setIsIOS(isIOSDevice);

        // Listen for install prompt (Android/Desktop)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Show prompt after a delay
            setTimeout(() => setShowPrompt(true), 3000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // For iOS, show custom prompt after delay
        if (isIOSDevice && !window.matchMedia('(display-mode: standalone)').matches) {
            setTimeout(() => setShowPrompt(true), 5000);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setIsInstalled(true);
            }
            setDeferredPrompt(null);
        }
        setShowPrompt(false);
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        // Don't show again for this session
        sessionStorage.setItem('pwa-prompt-dismissed', 'true');
    };

    // Don't show if already installed or dismissed
    if (isInstalled || !showPrompt || sessionStorage.getItem('pwa-prompt-dismissed')) {
        return null;
    }

    return (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-zinc-900 border border-neon-green/30 rounded-xl p-4 shadow-2xl z-50 animate-in slide-in-from-bottom-5">
            <button 
                onClick={handleDismiss}
                className="absolute top-2 right-2 text-zinc-500 hover:text-white"
            >
                <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-start gap-3">
                <div className="p-2 bg-neon-green/20 rounded-lg">
                    <Smartphone className="w-8 h-8 text-neon-green" />
                </div>
                <div className="flex-1">
                    <h3 className="font-bold text-white mb-1">Install VG Wallet</h3>
                    <p className="text-sm text-zinc-400 mb-3">
                        {isIOS 
                            ? 'Tap Share lalu "Add to Home Screen"'
                            : 'Install app untuk akses lebih cepat'
                        }
                    </p>
                    
                    {!isIOS && deferredPrompt && (
                        <Button 
                            onClick={handleInstallClick}
                            className="w-full bg-neon-green text-void-black hover:bg-neon-green/90"
                            size="sm"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Install Sekarang
                        </Button>
                    )}
                    
                    {isIOS && (
                        <div className="text-xs text-zinc-500 bg-zinc-800 rounded-lg p-2">
                            <p className="flex items-center gap-1 mb-1">
                                1. Tap <span className="text-neon-green">Share</span> button
                            </p>
                            <p className="flex items-center gap-1">
                                2. Tap <span className="text-neon-green">"Add to Home Screen"</span>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;
